import React, { useEffect, useRef } from 'react';
import { Smartphone, Users, Shield, Globe, Lightbulb, Clock } from 'lucide-react';
import Container from '../ui/Container';
import { Service } from '../../types';

const ServiceCard = ({ service }: { service: Service }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  // Map string icon names to actual icon components
  const IconComponent = () => {
    switch (service.icon) {
      case 'Smartphone':
        return <Smartphone size={24} className="text-orange-500" />;
      case 'Users':
        return <Users size={24} className="text-orange-500" />;
      case 'Shield':
        return <Shield size={24} className="text-orange-500" />;
      case 'Globe':
        return <Globe size={24} className="text-orange-500" />;
      case 'Lightbulb':
        return <Lightbulb size={24} className="text-orange-500" />;
      case 'Clock':
        return <Clock size={24} className="text-orange-500" />;
      default:
        return <Lightbulb size={24} className="text-orange-500" />;
    }
  };

  return (
    <div 
      ref={cardRef} 
      className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 opacity-0"
    >
      <div className="bg-orange-100 p-3 inline-block rounded-lg mb-4">
        <IconComponent />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
      <p className="text-gray-600">{service.description}</p>
    </div>
  );
};

const Services = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services: Service[] = [
    {
      title: 'Mobile Applications',
      description: 'Custom mobile solutions for iOS and Android that connect service providers with customers.',
      icon: 'Smartphone'
    },
    {
      title: 'Platform Development',
      description: 'Building robust platforms that enable seamless service delivery and management.',
      icon: 'Users'
    },
    {
      title: 'Security Solutions',
      description: 'Implementing advanced security measures to protect user data and transactions.',
      icon: 'Shield'
    },
    {
      title: 'Global Connectivity',
      description: 'Creating solutions that work across regions with localization support.',
      icon: 'Globe'
    },
    {
      title: 'Innovation Consulting',
      description: 'Strategic guidance on leveraging technology for business growth and efficiency.',
      icon: 'Lightbulb'
    },
    {
      title: 'On-Demand Services',
      description: 'Specialized platforms for time-sensitive services with real-time tracking.',
      icon: 'Clock'
    }
  ];

  return (
    <section id="services" className="py-16 md:py-24">
      <Container>
        <div ref={sectionRef} className="text-center mb-12 opacity-0 transition-opacity duration-1000">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
          <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We offer a range of technology solutions designed to connect service providers with customers, creating efficient and seamless experiences.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Services;